import type { UploadsController } from '../client-tickets/use-uploads';

import {
  EyeIcon,
  GlobeHemisphereWestIcon,
  LockIcon,
} from '@phosphor-icons/react';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';
import { color } from 'src/constants/color.css';

import { Api, useApiFormSubmit } from '../../api';
import {
  Avatar,
  Button,
  DateWithTooltip,
  MarkdownText,
  Modal,
  Stack,
  Tabs,
  Text,
} from '../../components';
import { Style } from '../../constants';
import { useClientData } from '../../contexts';
import { FormWrap, createFormValidator } from '../../form';
import { useAuthUser } from '../../hooks';
import { isAgency } from '../../reducers/auth';
import FieldUploads from '../client-tickets/components/FieldUploads';
import TiptapEditor from '../client-tickets/components/TiptapEditor';

export type ComposerActionHelpers = {
  submit: () => void;
  bodyValue: string;
};

type Props = Readonly<{
  endpoint: string;
  relationKey: string;
  relationValue: string;
  invalidate: () => unknown;
  /** Rendered above the actions row, e.g. the ticket's label checkboxes. */
  extraControls?: React.ReactNode;
  /** Leading actions-row buttons, given the submit handler, e.g. Close Ticket. */
  extraActions?: (helpers: ComposerActionHelpers) => React.ReactNode;
  /** Runs after a comment posts successfully, e.g. ticket label updates. */
  onPosted?: () => void | Promise<void>;
}>;

export default function CommentComposer({
  endpoint,
  relationKey,
  relationValue,
  invalidate,
  extraControls,
  extraActions,
  onPosted,
}: Props) {
  const { user } = useClientData();
  const authUser = useAuthUser();
  const showInternalTab = isAgency(authUser);

  const [isInternal, setIsInternal] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);
  const uploads = React.useRef<UploadsController | null>(null);

  const formSubmit = useApiFormSubmit('POST', endpoint, { invalidate });
  const { formErrorList, getField, handleSubmit, isLoading } = useForm<
    Partial<Api.Comment.W>
  >({
    defaultValues: { body: '', uploads: [] },
    validator: createFormValidator({
      body: { strictPresence: true },
      uploads: { upload: true },
    }),
    onSubmit: async (values, ...rest): Promise<void> => {
      const posted = await formSubmit(
        { ...values, isInternal, [relationKey]: relationValue },
        ...rest
      );
      if (posted) await onPosted?.();
    },
  });

  const bodyField = getField('body');

  return (
    <Stack gap={1.5}>
      <FormWrap formErrorList={formErrorList} handleSubmit={handleSubmit}>
        <Stack padH={1.5} padV={1.5} style={styles.composeCard} gap={1}>
          <html.div style={[Style.row, Style.alignCenter, styles.header]}>
            <Avatar user={user} size={32} />
            <html.div style={[Style.row, Style.alignCenter, styles.headerInfo]}>
              <Text typeface="body.medium">
                {user ? `${user.firstName} ${user.lastName}` : 'Account'}
              </Text>
            </html.div>
          </html.div>

          <Stack gap={0.5}>
            {showInternalTab && (
              <html.div style={styles.tabBar}>
                <Tabs
                  tabs={[
                    {
                      label: 'Reply to Client',
                      icon: (props) => <GlobeHemisphereWestIcon {...props} />,
                      selected: !isInternal,
                      onClick: () => setIsInternal(false),
                    },
                    {
                      label: 'Internal Note',
                      icon: (props) => <LockIcon {...props} />,
                      selected: isInternal,
                      onClick: () => setIsInternal(true),
                    },
                  ]}
                />
              </html.div>
            )}

            <TiptapEditor
              value={bodyField.value ?? ''}
              onChange={bodyField.handleValueChange}
              onPasteFiles={(files) =>
                uploads.current?.addPastedFiles(files) ?? false
              }
            />
          </Stack>

          <FieldUploads field={getField('uploads')} controllerRef={uploads} />

          {extraControls}

          <html.div
            style={[
              Style.row,
              Style.alignCenter,
              Style.spaceBetween,
              styles.actionRow,
            ]}
          >
            <html.div
              style={[Style.row, Style.alignCenter, styles.actionButtons]}
            >
              {extraActions?.({
                submit: handleSubmit,
                bodyValue: bodyField.value ?? '',
              })}
              <Button
                type="submit"
                disabled={isLoading || bodyField.value === ''}
                label={isInternal ? 'Post Internal Note' : 'Post Comment'}
                style={styles.fullWidthOnMobile}
              />
            </html.div>
            <Button
              type="button"
              label="Preview"
              variant="text"
              iconLeft={(p) => <EyeIcon {...p} />}
              onClick={() => setShowPreview(true)}
              disabled={bodyField.value === ''}
              style={styles.fullWidthOnMobile}
            />
          </html.div>
        </Stack>
      </FormWrap>
      {showPreview && (
        <Modal
          title="Preview"
          onClose={() => setShowPreview(false)}
          fullScreenOnMobile
        >
          <Stack padH={1.5} padV={1.5} gap={1}>
            <Stack style={[Style.row, Style.alignCenter]} gap={0.5}>
              <Avatar user={user} size={32} />
              <Text typeface="body.medium">
                {user ? `${user.firstName} ${user.lastName}` : 'Account'}
              </Text>
              <Text typeface="body.small.regular" color="neutral70">
                <DateWithTooltip
                  timestamp={new Date(
                    Date.now() - 10 * 60 * 1000
                  ).toISOString()}
                />
              </Text>
            </Stack>
            <MarkdownText body={bodyField.value ?? ''} />
          </Stack>
        </Modal>
      )}
    </Stack>
  );
}

const styles = css.create({
  composeCard: {
    backgroundColor: color.neutral0,
    borderRadius: 12,
    boxShadow: '0px 16px 16px 0px rgba(0, 0, 0, 0.04)',
  },
  header: {
    gap: 8,
  },
  headerInfo: {
    gap: 8,
  },
  actionRow: {
    flexDirection: {
      default: 'row',
      '@media (max-width: 768px)': 'column',
    },
    alignItems: {
      default: 'center',
      '@media (max-width: 768px)': 'stretch',
    },
    gap: {
      default: 0,
      '@media (max-width: 768px)': 16,
    },
  },
  actionButtons: {
    gap: 16,
    // Reversed so Post Comment sits above the other actions on mobile.
    flexDirection: {
      default: 'row',
      '@media (max-width: 768px)': 'column-reverse',
    },
    alignItems: {
      default: 'center',
      '@media (max-width: 768px)': 'stretch',
    },
  },
  fullWidthOnMobile: {
    width: {
      default: 'auto',
      '@media (max-width: 768px)': '100%',
    },
  },
  tabBar: {
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
  },
});
